// TermsAndConditionsComplete.js - Complete Terms and Conditions page for Travel+ CRM
import React, { useEffect } from 'react';

const TermsAndConditionsComplete = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <a href="/" className="flex items-center">
                <img 
                  src="/uploads/logos/Travel+ CRM Logo.png" 
                  alt="Travel+ CRM Logo" 
                  className="h-12 w-auto mr-3"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden items-center" style={{display: 'none'}}>
                  <div className="bg-blue-600 rounded-lg p-2 mr-2">
                    <span className="text-white font-bold text-lg">T+</span>
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">Travel+ CRM</h1>
                </div>
              </a>
            </div>
            <a 
              href="/" 
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              Back to Home
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 inline-block">
            <p className="text-blue-800 font-medium">Effective Date: June 1, 2025</p>
            <p className="text-blue-600 text-sm">Last Updated: November 20, 2025 | Version 2.0</p>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Table of Contents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <button onClick={() => scrollToSection('section-1')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">1. Acceptance of Terms</button>
            <button onClick={() => scrollToSection('section-2')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">2. Definitions</button>
            <button onClick={() => scrollToSection('section-3')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">3. User Roles and Permissions</button>
            <button onClick={() => scrollToSection('section-4')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">4. Data Privacy and Security</button>
            <button onClick={() => scrollToSection('section-5')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">5. Financial Terms</button>
            <button onClick={() => scrollToSection('section-6')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">6. Agent Websites</button>
            <button onClick={() => scrollToSection('section-7')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">7. Communication</button>
            <button onClick={() => scrollToSection('section-8')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">8. Technical Requirements</button>
            <button onClick={() => scrollToSection('section-9')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">9. Intellectual Property</button>
            <button onClick={() => scrollToSection('section-10')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">10. Prohibited Activities</button>
            <button onClick={() => scrollToSection('section-11')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">11. Account Management</button>
            <button onClick={() => scrollToSection('section-12')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">12. Limitation of Liability</button>
            <button onClick={() => scrollToSection('section-13')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">13. Indemnification</button>
            <button onClick={() => scrollToSection('section-14')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">14. Dispute Resolution</button>
            <button onClick={() => scrollToSection('section-15')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">15. Modifications</button>
            <button onClick={() => scrollToSection('section-16')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">16. Compliance</button>
            <button onClick={() => scrollToSection('section-17')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">17. Support Services</button>
            <button onClick={() => scrollToSection('section-18')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">18. International Users</button>
            <button onClick={() => scrollToSection('section-19')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">19. Accessibility</button>
            <button onClick={() => scrollToSection('section-20')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">20. Environmental</button>
            <button onClick={() => scrollToSection('section-21')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">21. Contact Information</button>
            <button onClick={() => scrollToSection('section-22')} className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1">22. Severability</button>
          </div>
        </div>

        {/* Terms Content */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 sm:p-8 prose prose-gray max-w-none">
            
            {/* Section 1 */}
            <section id="section-1" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">1. ACCEPTANCE OF TERMS</h2>
              <p className="text-gray-700 leading-relaxed">
                By accessing or using the Travel+ CRM platform ("Platform" or "Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you may not access or use the Service. These Terms apply to all users, including travel agents, agency administrators, independent agents, and clients.
              </p>
            </section>

            {/* Section 2 */}
            <section id="section-2" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">2. DEFINITIONS</h2>
              <div className="space-y-3">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>"Platform" or "Service":</strong> The Travel+ CRM web application, agent websites, mobile interfaces, and all related services</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>"Company" or "We":</strong> Northridge Ventures, LLC, the operator of the Travel+ CRM platform</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>"User":</strong> Any individual accessing the platform in any capacity</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>"Agent":</strong> Travel agents affiliated with partner agencies using the platform</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>"Independent Agent":</strong> Self-employed travel agents using the platform with direct subscriptions</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>"Agency Admin":</strong> Administrative users managing agency operations and agent oversight</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>"Agent Website":</strong> Custom subdomain websites created for agents (e.g., agentname.tpcrm.io)</p>
                </div>
              </div>
            </section>

            {/* Section 3 */}
            <section id="section-3" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">3. USER ROLES AND PERMISSIONS</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Travel Agents</h3>
                  <div className="bg-green-50 p-4 rounded-lg mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Permitted Activities:</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Submit, track, and manage commission requests with full audit trail</li>
                      <li>Access approved supplier directories and booking tools</li>
                      <li>Manage client information, travel profiles, and booking history</li>
                      <li>Create and send professional travel quotes with multiple components</li>
                      <li>Build and manage detailed travel itineraries with real-time updates</li>
                      <li>Create and maintain personal agent websites with custom subdomains</li>
                    </ul>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Independent Agents</h3>
                  <p className="text-gray-700">All standard agent activities plus subscription management and enhanced features.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 Agency Administrators</h3>
                  <p className="text-gray-700">Manage agent applications, approvals, commission requests, and agency-wide reporting.</p>
                </div>
              </div>
            </section>

            {/* Section 4 */}
            <section id="section-4" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">4. DATA PRIVACY AND SECURITY</h2>
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Data Protection Measures:</h4>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>All data encrypted in transit using TLS/SSL protocols</li>
                    <li>Multi-factor authentication available for enhanced security</li>
                    <li>Role-based access controls limit data visibility</li>
                    <li>Regular security audits and vulnerability assessments</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 5 - Financial Terms (Key Section) */}
            <section id="section-5" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">5. FINANCIAL TERMS AND COMMISSION PROCESSING</h2>
              
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Travel+ CRM Subscription Services</h3>
                
                <div className="space-y-6">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Subscription Plans Available:</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li><strong>Monthly Plan:</strong> Current pricing as displayed in platform</li>
                      <li><strong>Yearly Plan:</strong> Discounted annual pricing</li>
                      <li><strong>Agency Plans:</strong> Custom pricing for multiple agents</li>
                      <li><strong>Enterprise Plans:</strong> Tailored solutions for large organizations</li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Billing and Payment:</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Automatic renewal unless cancelled before renewal date</li>
                      <li>Secure payment processing with industry-standard encryption</li>
                      <li>Credit card, debit card, and ACH payments accepted</li>
                      <li>30 days advance notice for pricing changes</li>
                    </ul>
                  </div>
                  
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Cancellation and Refunds:</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Cancel anytime through account settings</li>
                      <li>Cancellation effective at end of current billing period</li>
                      <li>7-day full refund for new subscribers</li>
                      <li>Data export available for 30 days after cancellation</li>
                    </ul>
                  </div>
                  
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Account Suspension:</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>7-day grace period for failed payments</li>
                      <li>Account suspended after 7 days of non-payment</li>
                      <li>Account terminated after 30 days of non-payment</li>
                      <li>Service restored immediately upon successful payment</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">5.4 Payment Processing and Client Billing</h3>
                
                {/* Payment Liability Warning */}
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-lg font-medium text-red-800">IMPORTANT: Payment Processing Liability</h4>
                      <p className="text-red-700 mt-1">Agents are solely responsible for all aspects of client payment collection, processing, and handling. Travel+ CRM assumes no liability for payment processing activities.</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Agent Assumes All Payment Risks Including:</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Credit card fraud and chargebacks</li>
                      <li>Payment disputes and refund requests</li>
                      <li>Compliance with payment card industry standards</li>
                      <li>Data breaches related to payment information</li>
                      <li>Financial losses from payment processing errors</li>
                      <li>Legal claims arising from payment collection activities</li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">PCI Compliance Disclaimer</h4>
                    <p className="text-gray-700 mb-2"><strong>Travel+ CRM does not claim to be PCI DSS compliant.</strong></p>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Agents are solely responsible for PCI compliance in their payment activities</li>
                      <li>Agents must use PCI-compliant payment processors</li>
                      <li>Agents must implement appropriate security measures for payment data</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 6 */}
            <section id="section-6" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">6. AGENT WEBSITES AND SUBDOMAIN SERVICES</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Agents may create personalized websites with custom subdomains, SSL certificates, and mobile-responsive design.</p>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Website Features:</h4>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Custom subdomains (agentname.tpcrm.io)</li>
                    <li>SSL certificates for secure connections</li>
                    <li>Content management system</li>
                    <li>Lead capture forms integrated with CRM</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 7 */}
            <section id="section-7" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">7. COMMUNICATION AND MESSAGING</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Real-time messaging system for secure agent-admin communications with WebSocket-based notifications.</p>
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Communication Features:</h4>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Real-time messaging system</li>
                    <li>Email and SMS notifications</li>
                    <li>Mobile push notifications</li>
                    <li>Automated email templates</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 8 */}
            <section id="section-8" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">8. TECHNICAL REQUIREMENTS AND SYSTEM AVAILABILITY</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Platform operates 24/7 with 99.5% uptime targets and comprehensive backup procedures.</p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">System Requirements:</h4>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Modern web browser with JavaScript enabled</li>
                    <li>Stable internet connection</li>
                    <li>Mobile-responsive design support</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 9 */}
            <section id="section-9" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">9. INTELLECTUAL PROPERTY RIGHTS</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Travel+ CRM platform and all related intellectual property owned by Company. Users retain ownership of their business data and original content.</p>
              </div>
            </section>

            {/* Section 10 */}
            <section id="section-10" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">10. PROHIBITED ACTIVITIES AND ACCEPTABLE USE</h2>
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Users may not:</h4>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Violate any applicable laws or regulations</li>
                    <li>Impersonate other users or provide false information</li>
                    <li>Attempt unauthorized access to system components</li>
                    <li>Interfere with platform operation or security</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 11 */}
            <section id="section-11" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">11. ACCOUNT MANAGEMENT AND TERMINATION</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Accounts may be suspended or terminated for violation of Terms, non-payment, or fraudulent activity.</p>
              </div>
            </section>

            {/* Section 12 */}
            <section id="section-12" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">12. LIMITATION OF LIABILITY AND WARRANTIES</h2>
              <div className="space-y-4">
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Service Disclaimers:</h4>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Platform provided "as is" and "as available"</li>
                    <li>No guarantee of uninterrupted service</li>
                    <li>Company liability limited to subscription fees</li>
                    <li><strong>NO LIABILITY FOR PAYMENT PROCESSING</strong></li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 13 */}
            <section id="section-13" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">13. INDEMNIFICATION AND LEGAL PROTECTION</h2>
              <div className="space-y-4">
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Users agree to indemnify Company from claims arising from:</h4>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>User's violation of Terms or applicable laws</li>
                    <li>User's business activities through the platform</li>
                    <li><strong>Payment processing activities</strong></li>
                    <li><strong>PCI compliance failures</strong></li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 14 - Dispute Resolution (Key Section) */}
            <section id="section-14" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">14. DISPUTE RESOLUTION AND GOVERNING LAW</h2>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-lg font-medium text-red-800">PLEASE READ THIS SECTION CAREFULLY</h4>
                    <p className="text-red-700 mt-1 font-semibold">IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">14.1 Governing Law</h3>
                  <p className="text-gray-700">These Terms and Conditions and your use of Travel+ CRM are governed by and construed in accordance with the laws of the State of Utah, without regard to its conflict of law principles.</p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">14.2 Informal Negotiations</h3>
                  <p className="text-gray-700">To expedite resolution and control the cost of any dispute, controversy, or claim related to these Terms (each a "Dispute" and collectively, the "Disputes") brought by either you or us (individually, a "Party" and collectively, the "Parties"), the Parties agree to first attempt to negotiate any Dispute (except those Disputes expressly provided below) informally for at least thirty (30) days before initiating arbitration. Such informal negotiations commence upon written notice from one Party to the other.</p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">14.3 Binding Arbitration</h3>
                  <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                    <p className="text-gray-700 font-semibold">If the Parties are unable to resolve a Dispute through informal negotiations, the Dispute (except those Disputes expressly excluded below) will be finally and exclusively resolved by binding arbitration. <span className="text-red-600">YOU UNDERSTAND THAT WITHOUT THIS PROVISION, YOU WOULD HAVE THE RIGHT TO SUE IN COURT AND HAVE A JURY TRIAL.</span></p>
                  </div>
                  <p className="text-gray-700">The arbitration shall be commenced and conducted under the Commercial Arbitration Rules of the American Arbitration Association (AAA) and, where appropriate, the AAA's Supplementary Procedures for Consumer Related Disputes (AAA Consumer Rules), both of which are available at the AAA website (www.adr.org). Your arbitration fees and your share of arbitrator compensation shall be governed by the AAA Consumer Rules and, where appropriate, limited by the AAA Consumer Rules.</p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">14.4 Arbitration Location (Venue)</h3>
                  <p className="text-gray-700">The arbitration shall be conducted in Washington County, Utah, unless the Parties agree otherwise or the AAA rules dictate a different procedure (such as a telephone or desk arbitration for smaller claims). If for any reason a Dispute proceeds in court rather than arbitration, the dispute shall be commenced or prosecuted in the state and federal courts located in Washington County, Utah, and the Parties hereby consent to, and waive all defenses of lack of personal jurisdiction and forum non conveniens with respect to venue and jurisdiction in such state and federal courts.</p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">14.5 Class Action Waiver</h3>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-gray-700">The Parties agree that any arbitration shall be limited to the Dispute between the Parties individually. To the full extent permitted by law, (a) no arbitration shall be joined with any other proceeding; (b) there is no right or authority for any Dispute to be arbitrated on a class-action basis or to utilize class action procedures; and (c) there is no right or authority for any Dispute to be brought in a purported representative capacity on behalf of the general public or any other persons. <span className="font-semibold text-red-600">ALL DISPUTES MUST BE BROUGHT IN THE PARTIES' INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.</span></p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">14.6 Jury Trial Waiver</h3>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-gray-700 font-semibold"><span className="text-red-600">THE PARTIES HEREBY KNOWINGLY AND VOLUNTARILY WAIVE THEIR CONSTITUTIONAL AND STATUTORY RIGHTS TO GO TO COURT AND HAVE A TRIAL IN FRONT OF A JUDGE OR A JURY</span>, electing instead that all claims and disputes shall be resolved by arbitration. Arbitration procedures are typically more limited, more efficient, and less costly than rules applicable in a court and are subject to very limited review by a court.</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">14.7 Small Claims Exception</h3>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-gray-700">Notwithstanding the foregoing, either Party may bring an individual action in small claims court for disputes or claims within the scope of that court's jurisdiction, provided the action remains in that court and is not removed or appealed to a court of general jurisdiction.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 15 */}
            <section id="section-15" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">15. MODIFICATIONS AND UPDATES</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Terms may be updated periodically to reflect platform changes, legal requirements, or business needs. Material changes require notice to users.</p>
              </div>
            </section>

            {/* Section 16 */}
            <section id="section-16" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">16. COMPLIANCE AND REGULATORY REQUIREMENTS</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Users must comply with applicable travel industry regulations, tax compliance, and data protection laws including GDPR and CCPA.</p>
              </div>
            </section>

            {/* Section 17 */}
            <section id="section-17" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">17. SUPPORT SERVICES AND CUSTOMER SERVICE</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Technical support available through platform messaging, email, and phone during business hours with emergency support for critical issues.</p>
              </div>
            </section>

            {/* Section 18 */}
            <section id="section-18" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">18. INTERNATIONAL USERS AND CROSS-BORDER SERVICES</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Platform available to users in multiple countries subject to local law compliance with currency display and time zone support.</p>
              </div>
            </section>

            {/* Section 19 */}
            <section id="section-19" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">19. ACCESSIBILITY AND EQUAL ACCESS</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Platform designed with accessibility standards and best practices in mind, complying with WCAG, ADA, and other accessibility guidelines.</p>
              </div>
            </section>

            {/* Section 20 */}
            <section id="section-20" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">20. ENVIRONMENTAL AND SUSTAINABILITY CONSIDERATIONS</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Platform infrastructure designed for energy efficiency and environmental sustainability with paperless operations encouraged.</p>
              </div>
            </section>

            {/* Contact Section */}
            <section id="section-21" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">21. CONTACT INFORMATION AND SUPPORT</h2>
              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">For questions about these Terms:</h4>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Platform Support:</strong> Available through in-platform messaging system</p>
                  <p><strong>Email Support:</strong> support@travelpluscrm.com</p>
                  <p><strong>Phone Support:</strong> [Support phone number to be specified by Company]</p>
                </div>
              </div>
            </section>

            {/* Section 22 */}
            <section id="section-22" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">22. SEVERABILITY AND ENFORCEABILITY</h2>
              <div className="space-y-4">
                <p className="text-gray-700">If any provision of these Terms is found unenforceable, the remaining provisions will remain in full force and effect.</p>
              </div>
            </section>

            {/* Section 23 */}
            <section id="section-23" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">23. WAIVER AND MODIFICATION</h2>
              <div className="space-y-4">
                <p className="text-gray-700">No waiver of any provision constitutes a continuing waiver. These Terms may only be modified by written agreement.</p>
              </div>
            </section>

            {/* Section 24 */}
            <section id="section-24" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">24. ENTIRE AGREEMENT AND INTEGRATION</h2>
              <div className="space-y-4">
                <p className="text-gray-700">These Terms constitute the entire agreement between users and Company regarding the Service and supersede all prior agreements.</p>
              </div>
            </section>

            {/* Section 25 */}
            <section id="section-25" className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">25. SURVIVAL OF TERMS</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Provisions that by their nature should survive termination will survive, including intellectual property rights, indemnification obligations, and liability limitations.</p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="mb-8">
              <div className="bg-gray-900 text-white p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">ACKNOWLEDGMENT AND ACCEPTANCE</h3>
                <p className="leading-relaxed">
                  <strong>By creating an account, accessing the platform, or using any platform services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions in their entirety.</strong>
                </p>
                <p className="mt-3 text-gray-300">
                  If you do not agree to these Terms, you must immediately discontinue use of the platform and may not access or use any platform services.
                </p>
              </div>
            </section>

            {/* Document Info */}
            <section className="border-t border-gray-200 pt-6">
              <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600">
                <p><strong>Document Information:</strong></p>
                <ul className="mt-2 space-y-1">
                  <li>Effective Date: June 1, 2025</li>
                  <li>Last Updated: November 20, 2025</li>
                  <li>Version: 2.0</li>
                  <li>Document Type: Terms and Conditions</li>
                </ul>
                <p className="mt-3 text-xs">
                  <strong>Legal Notice:</strong> These Terms and Conditions have been prepared to reflect the current state of the Travel+ CRM platform and applicable legal requirements. Users are advised to review these Terms regularly and consult with qualified legal counsel for specific legal advice related to their use of the platform and business activities.
                </p>
              </div>
            </section>

          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">
              © 2025 Northridge Ventures, LLC. All Rights Reserved
            </p>
            <div className="flex justify-center space-x-4">
              <a 
                href="/terms-and-conditions" 
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                Terms and Conditions
              </a>
              <span className="text-gray-300">|</span>
              <a 
                href="/privacy-policy" 
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                Privacy Policy
              </a>
              <span className="text-gray-300">|</span>
              <a 
                href="/" 
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                Back to Home
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditionsComplete;
